import { stylize } from "@envestnet/envreact-component-library";
import createActivityDetector from "activity-detector";
import classnames from "classnames";
import DashboardFooter from "components/DashboardFooter";
import React, { useEffect, useState } from "react";
import withIntl from "utils/withIntl";
import SessionTimeoutDialog from "./SessionTimeoutDialog";
import styles, { Styles } from "./styles";
import DashboardHeader from "components/DashboardHeader";
import { UserInfo } from "types/UserInfo";

interface InjectedProps extends Styles {
  /** Internal - injected from WithStyles */
  innerRef?: React.RefObject<HTMLDivElement>;
  loadingDashboard: boolean;
}

interface DefaultProps {
  // Default props should always be required props, i.e. no ? indicating it's optional
  // The default values are specified below
}

function formatTimesLeft(remaining: number) {
  const minutes = Math.floor(Math.round(remaining) / 60);
  const seconds = Math.round(remaining) % 60;

  const tokens: string[] = [];
  if (minutes) {
    tokens.push(`${minutes} minutes`);
  }
  tokens.push(`${seconds} seconds`);

  return tokens.join(" ");
}

export interface DashboardContainerProps extends DefaultProps {
  /** CSS class name to be applied to top level component */
  className?: string;

  /** Inline style object to be applied to top level component */
  style?: React.CSSProperties;

  goToSecuritySettings: () => void;
  logout: () => void;
  setUserAcceptedNewTnC: () => void;

  children: JSX.Element;
  user: UserInfo;
  isDirectLoginEnabled: boolean;

  tncForCidpUser: string | null;
  hasUpdatedTnCAccepted: boolean;
}

const defaultProps: DefaultProps = {};

type Props = InjectedProps & DashboardContainerProps;

const DashboardContainer = (props: Props) => {
  const {
    classes,
    className,
    innerRef,
    children,
    logout,
    goToSecuritySettings,
    user,
    isDirectLoginEnabled,
    setUserAcceptedNewTnC,
    loadingDashboard,
  } = props;

  const [appIdle, setAppIdle] = useState<boolean>(false);
  const [sessionExpired, setSessionExpired] = useState<boolean>(false);
  const [timesLeftMessage, setTimesLeftMessage] = useState<string>("");
  const [counter, setCounter] =
    React.useState<ReturnType<typeof setInterval>>();

  useEffect(() => {
    const activityDetector = createActivityDetector({
      timeToIdle: 28 * 60 * 1000, // 28 minutes
      inactivityEvents: [],
    });
    activityDetector.on("idle", () => {
      setAppIdle(true);
    });

    return () => activityDetector.stop();
  }, []);

  // Session expiry modal timer
  useEffect(() => {
    if (appIdle) {
      // counter stops when device goes to sleep. using timestamp will resolve that problem.
      const expireTimestamp = new Date().getTime() + 120 * 1000;
      let timesLeft = expireTimestamp - new Date().getTime();
      setTimesLeftMessage(formatTimesLeft(timesLeft / 1000));
      const handle = setInterval(() => {
        timesLeft = expireTimestamp - new Date().getTime();
        if (timesLeft < 0) {
          setSessionExpired(true);
        } else {
          setTimesLeftMessage(formatTimesLeft(timesLeft / 1000));
        }
      }, 1000);
      setCounter(handle);

      return () => clearInterval(handle);
    }

    return () => setCounter(undefined);
  }, [appIdle]);

  return (
    <div ref={innerRef} className={classnames(className, classes.container)}>
      <DashboardHeader
        signout={logout}
        goToSecuritySettings={goToSecuritySettings}
        user={user}
        isDirectLoginEnabled={isDirectLoginEnabled}
        loadingDashboard={loadingDashboard}
      />
      <div className={classes.body}>
        <div className={classes.content}>
          {children}
          <div className={classes.footer}>
            <DashboardFooter
              acceptUpdatedTnC={setUserAcceptedNewTnC}
              {...props}
            />
          </div>
        </div>
        <div className={classes.dashboardImage}></div>
      </div>
      <SessionTimeoutDialog
        dialogContent={timesLeftMessage}
        counter={counter}
        open={appIdle}
        setAppIdle={setAppIdle}
        logout={logout}
        isSessionExpired={sessionExpired}
      />
    </div>
  );
};

DashboardContainer.defaultProps = defaultProps;

export default withIntl(
  stylize<Props, DefaultProps>(styles)(DashboardContainer)
);
